import { ProjectLayout } from '../../layouts/project-layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { router } from '@inertiajs/react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { IntegrationsTab } from '../../components/conversations/IntegrationsTab'
import { DesignConceptPicker, type DesignConcept } from '../../components/projects/DesignConceptPicker'
import type { Project } from '@/types/conversations'
import { ArchiveIcon, ArchiveRestoreIcon, Settings as SettingsIcon, Plug, Palette } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../components/ui/alert-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ProjectSettingsProps {
  project: Project & {
    design_concept?: string | null
  }
  design_concepts: DesignConcept[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

function Settings({ project, design_concepts, auth }: ProjectSettingsProps) {
  const { t } = useTranslation(['projects', 'common'])
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const [isUnarchiveDialogOpen, setIsUnarchiveDialogOpen] = useState(false)
  const isAdmin = auth.user.admin
  const isManagedAgentProject = project.project_type === 'managed_agent'

  const handleArchive = () => {
    router.post(`/projects/${project.id}/archive`)
    setIsArchiveDialogOpen(false)
  }

  const handleUnarchive = () => {
    router.post(`/projects/${project.id}/unarchive`)
    setIsUnarchiveDialogOpen(false)
  }
  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">

              {/* Page Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-foreground">Project Settings</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Manage your project configuration and preferences
                </p>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="mb-6">
                  <TabsTrigger value="general" className="gap-2">
                    <SettingsIcon className="h-4 w-4" />
                    General
                  </TabsTrigger>
                  {!isManagedAgentProject && (
                    <TabsTrigger value="design" className="gap-2">
                      <Palette className="h-4 w-4" />
                      {t('projects:settings.design.tab')}
                    </TabsTrigger>
                  )}
                  {!isManagedAgentProject && (
                    <TabsTrigger value="integrations" className="gap-2">
                      <Plug className="h-4 w-4" />
                      Integrations
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* General Settings Tab */}
                <TabsContent value="general">
                  <div className="space-y-6">
                    {/* Archive Section - Admin Only */}
                    {isAdmin && (
                      <Card className="w-full shadow-none">
                        <CardHeader>
                          <CardTitle>Archive Project</CardTitle>
                          <CardDescription>
                            {project.is_archived
                              ? 'This project is archived. Unarchive it to continue working on it.'
                              : 'Archive this project to hide it from your active projects list.'}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {project.is_archived ? (
                            <AlertDialog open={isUnarchiveDialogOpen} onOpenChange={setIsUnarchiveDialogOpen}>
                              <AlertDialogTrigger asChild>
                                <Button variant="default">
                                  <ArchiveRestoreIcon className="mr-2 h-4 w-4" />
                                  Unarchive Project
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Unarchive Project</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to unarchive this project? This will restore it to your active projects list and you'll be able to create new features again.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
                                  <AlertDialogAction onClick={handleUnarchive}>
                                    Unarchive
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
                              <AlertDialogTrigger asChild>
                                <Button variant="destructive">
                                  <ArchiveIcon className="mr-2 h-4 w-4" />
                                  Archive Project
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Archive Project</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to archive this project? This will hide it from your active projects list and prevent any new features from being created. You can unarchive it later to resume work.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
                                  <AlertDialogAction onClick={handleArchive}>
                                    Archive
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>

                {/* Design Tab */}
                {!isManagedAgentProject && (
                  <TabsContent value="design">
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-lg font-semibold">{t('projects:settings.design.title')}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('projects:settings.design.description')}
                        </p>
                      </div>
                      <DesignConceptPicker
                        concepts={design_concepts}
                        currentConcept={project.design_concept ?? null}
                        projectId={project.id}
                      />
                    </div>
                  </TabsContent>
                )}

                {/* Integrations Tab */}
                {!isManagedAgentProject && (
                  <TabsContent value="integrations">
                    <IntegrationsTab project={project} />
                  </TabsContent>
                )}

              </Tabs>

            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Settings.layout = (page: React.ReactNode) => <ProjectLayout>{page}</ProjectLayout>

export default Settings
